import { AxiosError } from 'axios'
import { Button, Icon, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import {
  assinaturaDigitalCancelMessage,
  assinaturaDigitalSuccessMessage,
  defaultAssinaturaDigitalErrorMessage,
  DocumentoDigitalErrorCodeEnum,
  documentoDigitalErrorCodeToMessage,
  StatusAssinaturaDigitalPopup,
  TipoDocumentoAssinaturaDigitalPopup,
} from 'hooks/assinatura-digital/model-assinaturaDigitalPopup'
import { useAssinaturaDigitalPopup } from 'hooks/assinatura-digital/useAssinaturaDigitalPopup'
import { useSessionHasConselhoClasse } from 'hooks/conselho-classe/useSessionHasConselhoClasse'
import { useEffect, useRef, useState } from 'react'

import {
  AssinaturaEletronicaAtendTableRowModel,
  GerarAtendimentoDigitalInput,
} from '../model-assinaturaEletronicaAtend'

interface AssinarAtendimentoDigitalButtonProps {
  readonly row: AssinaturaEletronicaAtendTableRowModel
  readonly refetch: () => void
}

export function AssinarAtendimentoDigitalButton({ row, refetch }: AssinarAtendimentoDigitalButtonProps) {
  const alert = useAlert()
  const sessionHasConselhoClasse = useSessionHasConselhoClasse()

  const [isAssinando, setIsAssinando] = useState(false)
  const popupDataIdentifierRef = useRef<string | null>(null)

  const { data: popupData, openPopup, closePopup } = useAssinaturaDigitalPopup<GerarAtendimentoDigitalInput>(
    { atendimentoId: row.id },
    TipoDocumentoAssinaturaDigitalPopup.ATENDIMENTO
  )

  useEffect(() => {
    if (isAssinando && popupData?.identifier !== popupDataIdentifierRef.current) {
      switch (popupData?.status) {
        case StatusAssinaturaDigitalPopup.SUCCESS:
          setIsAssinando(false)
          alert('success', assinaturaDigitalSuccessMessage)
          refetch()
          break
        case StatusAssinaturaDigitalPopup.CANCELED:
          setIsAssinando(false)
          alert('info', assinaturaDigitalCancelMessage)
          break
        case StatusAssinaturaDigitalPopup.FAILURE:
          setIsAssinando(false)
          alert(
            'danger',
            popupData?.error
              ? documentoDigitalErrorCodeToMessage[popupData.error]
              : defaultAssinaturaDigitalErrorMessage
          )
          break
      }
    }
    popupDataIdentifierRef.current = popupData?.identifier
  }, [alert, isAssinando, popupData, refetch])

  useEffect(() => {
    if (!isAssinando) {
      closePopup()
    }
  }, [isAssinando, closePopup])

  const handleClick = async () => {
    try {
      setIsAssinando(true)
      await openPopup({ atendimentoProfissionalId: row.id })
    } catch (error) {
      setIsAssinando(false)
      const axiosError = error as AxiosError<string>
      alert(
        'danger',
        axiosError.response?.status === 400 ? axiosError.response.data : defaultAssinaturaDigitalErrorMessage
      )
    }
  }

  const isDisabled =
    row.isAtendimentoAssinado || isAssinando || !sessionHasConselhoClasse || row.isObservacaoNaoFinalizada

  const getTooltipText = () => {
    if (row.isAtendimentoAssinado) {
      return 'Registro já assinado'
    }
    if (row.isObservacaoNaoFinalizada) {
      return 'Não é possível assinar atendimentos de observação não finalizados.'
    }
    if (!sessionHasConselhoClasse) {
      return documentoDigitalErrorCodeToMessage[DocumentoDigitalErrorCodeEnum.PROFISSIONAL_SEM_CONSELHO] as string
    }
    return 'Assinar documento'
  }
  const tooltipText = getTooltipText()

  return (
    <Tooltip text={tooltipText}>
      <Button size='small' skin='ghost' disabled={isDisabled} onClick={handleClick}>
        <Icon icon='signature' fill='normal' />
      </Button>
    </Tooltip>
  )
}
